import { api } from '../services/axiosConfig';

/** One side of a kill. The ids are what the row turns into
 * images.evetech.net art — a ship render, a pilot portrait, corp and
 * alliance logos. All optional: a structure kill has no pilot, and plenty of
 * corps fly outside an alliance. */
export interface KillParty {
  name: string;
  characterId?: number;
  shipTypeName: string;
  shipTypeId?: number;
  corporationId?: number;
  corporationName?: string;
  allianceId?: number;
  allianceName?: string;
}

export interface ZkbKill {
  killmail_id: number;
  killmail_time: string;
  totalValue: number;
  attackerCount: number;
  victim: KillParty;
  finalBlow: KillParty | null;
}

export interface KillboardEntityActivity {
  id: number;
  name: string;
  kills: number;
}

export interface KillboardStats {
  totalKills: number;
  windowDays: number;
  /** zKillboard's feed ran out before reaching the window start — totals are a floor. */
  truncated: boolean;
  mostRecentKill: { timestamp: string; daysAgo: number } | null;
  hourlyActivity: number[]; // 24 buckets, UTC hour
  weekdayHourActivity: number[][]; // [7 weekdays (0 = Sunday)][24 UTC hours]
  /** One entry per UTC calendar day in the window, oldest first. Quiet days
   * are present with `total: 0` so the axis is a continuous timeline. */
  dailyActivity: DailyActivity[];
  /** Exact "now minus N" counts — a kill 20 hours ago counts toward 24h even
   * if it landed on a different UTC calendar day than `dailyActivity` buckets
   * it into. Each window is only as accurate as the feed actually fetched:
   * `days30` needs a 30-day request to mean anything. */
  rollingWindows: { hours24: number; days7: number; days30: number };
}

export interface DailyActivity {
  /** YYYY-MM-DD, UTC. */
  date: string;
  total: number;
  /** That day's own 24 UTC-hour histogram. */
  hours: number[];
}

export interface KillboardResponse {
  kills: ZkbKill[];
  stats: KillboardStats;
  activeCorps: KillboardEntityActivity[];
  activeAlliances: KillboardEntityActivity[];
}

export const fetchSystemKills = (solarSystemId: number, days: 7 | 14 | 30) =>
  api.get<KillboardResponse>(`/api/killboard/system/${solarSystemId}`, { params: { days } }).then((r) => r.data);

/** Kill values span from a 16k ISK noob ship to a 30B ISK titan, and the
 * difference between those two is most of what a killboard tells you — so
 * scale the unit instead of forcing everything through "M" (which rendered
 * every cheap gank as a useless "0.0M"). */
export function formatIsk(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B ISK`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M ISK`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}k ISK`;
  return `${Math.round(value)} ISK`;
}
